// src/pages/PickTeam.js
import React, { useEffect, useMemo, useState } from "react";
import Pitch from "../central/Pitch";
import "../central/PickTeam.css";
import "../central/PitchPages.css";
import { useParams, useNavigate } from "react-router-dom";
import {
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { setNavGuard } from "../utils/navGuard";
import { db } from "../lib/firebase";
import Panel from "../central/Panel";
import { PrimaryButton, SecondaryButton } from "../central/Button";
import { HeadingLarge, HeadingMedium, BodyText } from "../central/Typography";
import { useAuth } from "../context/AuthContext";
// import { col, ref } from "../lib/fw";
import { paths } from "../lib/paths";
import { getLeagueConfig } from "../utils/leagueConfig";
import { isUnlimitedFT } from "../utils/freeTransfers";
import { logError } from "../utils/logError";
import { logEvent } from "../utils/EventLogger";

import { useToast } from "../context/ToastContext";

import MobilePlayerDrawer from "../components/MobilePlayerDrawer";
import PlayerCardModal from "../components/PlayerCardModal";
import { setActiveSeasonID } from "../lib/seasonPaths";
import { useSeason } from "../context/SeasonContext";
import LoadingOverlay from "../central/LoadingOverlay";


export default function PickTeam() {
  const { leagueID } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const { showToast } = useToast();
  const { selectedSeasonID } = useSeason();

  const [selectedLeague, setSelectedLeague] = useState(null);
  const [resolvedSeasonID, setResolvedSeasonID] = useState(null);
  const [players, setPlayers] = useState([]);
  // Dynamic team state: initial structure matches POSITIONS
  // Dynamic team state: recompute config & positions when league loads
  const leagueConfig = useMemo(() => {
    return selectedLeague?.leagueType
      ? getLeagueConfig(selectedLeague.leagueType)
      : {};
  }, [selectedLeague]);
  // Use positionRules from squadConfig if present, else fallback to empty object
  const positionRules = leagueConfig.squadConfig?.positionRules || {};

  // Unified debug logger – always fires once both leagueConfig and selectedLeague are loaded
  useEffect(() => {
    if (!selectedLeague || !Object.keys(leagueConfig || {}).length) return;
    // Debug logs removed
  }, [selectedLeague, leagueConfig, positionRules]);

  // Force rerender of team UI when leagueConfig updates (e.g., 5-a-side vs 7-a-side)
  useEffect(() => {
    if (leagueConfig && Object.keys(leagueConfig).length > 0) {
      setTeam({});
      setTeamMeta({});
      setTeamError("");
    }
  }, [leagueConfig]);

const POSITIONS = useMemo(
  () => leagueConfig.positions || ["DEF", "MID", "FWD"],
  [leagueConfig]
);
  // Memoized initial team structure
  const initialTeamStructure = useMemo(() => {
    const obj = {};
    POSITIONS.forEach(pos => { obj[pos] = []; });
    return obj;
  }, [POSITIONS]);
  const [team, setTeam] = useState(initialTeamStructure);
  const [originalTeam, setOriginalTeam] = useState(null);
  // New: squad meta for prices, buyPrice, etc.
  const [teamMeta, setTeamMeta] = useState({});
  const [budget, setBudget] = useState(24.0);
  // New: baseBank for bankStart tracking
  const [baseBank, setBaseBank] = useState(null);
  const [currentGameweek, setCurrentGameweek] = useState(null);
  const [isLocked, setIsLocked] = useState(false);
  const [teamID, setTeamID] = useState(null);
  const [userName, setUserName] = useState("");
  const [userRole, setUserRole] = useState("");

  const [captainID, setCaptainID] = useState("");
  const [chipChoice, setChipChoice] = useState("None");
  const [chipExpanded, setChipExpanded] = useState(false);
  const [chipsUsed, setChipsUsed] = useState({});
  const [errorMsg, setErrorMsg] = useState("");
  // Team structure helper/error message
  const [teamError, setTeamError] = useState("");
  const [hasInteracted, setHasInteracted] = useState(false);
  const [transfersThisGW, setTransfersThisGW] = useState(0);
  const [transferPenalty, setTransferPenalty] = useState(0);
  const [maxFreeTransfers, setmaxFreeTransfers] = useState(0);
  // Saved base value of transfers from Firestore for this GW
  const [savedTransfersBase, setSavedTransfersBase] = useState(0);
  // New: startGW state
  const [startGW, setStartGW] = useState("GW1");
  const [isSaving, setIsSaving] = useState(false);
  // Save-state UI: what's saved for this GW, and a snapshot of the loaded state to diff against
  const [saveInfo, setSaveInfo] = useState(null); // { kind: "saved"|"carried"|"empty", at?, from? }
  const [savedState, setSavedState] = useState(null);
  const [loadedTick, setLoadedTick] = useState(0);
  const [pendingNav, setPendingNav] = useState(null); // () => void, set when leaving with unsaved changes
  const [lastWeekIds, setLastWeekIds] = useState(null); // player IDs in last GW's team, for NEW tags
  const [availablePlayerIDs, setAvailablePlayerIDs] = useState(new Set());

const CHIP_OPTIONS = useMemo(() => {
  const base = leagueConfig.chips || ["Goal Boost", "Triple Captain", "Fortress"];
  return ["None", ...base.filter((c) => c !== "None")];
}, [leagueConfig]);

  // Sync team structure if POSITIONS changes (ensure keys match)
  useEffect(() => {
    setTeam(prev => {
      // If shape is different, reset to empty structure
      const prevKeys = Object.keys(prev || {});
      const posKeys = POSITIONS;
      const needsReset =
        prevKeys.length !== posKeys.length ||
        !posKeys.every(k => prevKeys.includes(k));
      if (needsReset) {
        const obj = {};
        posKeys.forEach(pos => { obj[pos] = []; });
        return obj;
      }
      return prev;
    });
  }, [POSITIONS]);

  const byId = useMemo(() => {
    const m = new Map();
    players.forEach((p) => m.set(p.id, p));
    return m;
  }, [players]);

  const selectedPlayers = POSITIONS
    .flatMap((pos) => team[pos])
    .map((id) => byId.get(id))
    .filter(Boolean);

  // Resolve user's teamID for this league
  useEffect(() => {
    if (!currentUser) return;
    (async () => {
      const uRef = doc(db, "users", currentUser.uid);
      const uSnap = await getDoc(uRef);
      if (uSnap.exists()) {
        const u = uSnap.data();

        // For analytics + light admin debugging
        setUserName(typeof u.displayName === "string" ? u.displayName : "");
        setUserRole(leagueID && u.roles?.[leagueID] ? u.roles[leagueID] : "");

        if (leagueID && u.teams?.[leagueID]) {
          setTeamID(u.teams[leagueID]);
        }
      }
    })();
  }, [currentUser, leagueID]);

  // Resolve league + active season first so season-scoped paths read the correct season
  useEffect(() => {
    if (!currentUser || !leagueID) return;

    (async () => {
      try {
        const leagueSnap = await getDoc(paths.league(leagueID));
        if (!leagueSnap.exists()) return;

        const leagueData = leagueSnap.data() || {};
        setSelectedLeague({ id: leagueSnap.id, ...leagueData });

        const nextSeasonID =
          typeof leagueData.currentSeasonID === "string" && leagueData.currentSeasonID.trim() !== ""
            ? leagueData.currentSeasonID.trim()
            : "S1";

        setResolvedSeasonID(nextSeasonID);
        setActiveSeasonID(nextSeasonID);
      } catch (err) {
        console.error("Failed to resolve league season:", err);
      }
    })();
  }, [currentUser, leagueID]);

  // Fetch current gameweek for this league after the correct season has been resolved
  useEffect(() => {
    if (!currentUser || !leagueID || !resolvedSeasonID) return;

    (async () => {
      try {
        setActiveSeasonID(resolvedSeasonID);

        // Try meta doc first: leagues/{leagueID}/gameweeks/meta { current: "GW1" }
        const metaSnap = await getDoc(paths.gameweek(leagueID, "meta"));
        const meta = metaSnap.exists() ? metaSnap.data() : null;

        if (meta?.current) {
          setCurrentGameweek(meta.current);
          return;
        }

        // Fallback: where isCurrent == true
        const qGW = query(
          paths.leagueGameweeks(leagueID),
          where("isCurrent", "==", true)
        );
        const snapshot = await getDocs(qGW);

        if (!snapshot.empty) {
          const gwDoc = snapshot.docs[0];
          const data = gwDoc.data();
          const cur = data?.number ? `GW${data.number}` : gwDoc.id;
          setCurrentGameweek(cur);

          if (data?.deadline?.toDate) {
            setIsLocked(new Date() > data.deadline.toDate());
          }
        } else {
          // default if nothing exists yet
          setCurrentGameweek("GW1");
        }
      } catch (err) {
        console.error("Fetch current GW failed:", err);
      }
    })();
  }, [currentUser, leagueID, resolvedSeasonID]);
  const writeGwSnapshot = async ({
    leagueID,
    teamID,
    currentGameweek,
    team,
    captainID,
    chipChoice,
    currentUser,
  }) => {
    // /leagues/{leagueID}/gameweeks/{GW}/teams/{teamID}
    const snapRef = doc(paths.gwTeams(leagueID, currentGameweek), teamID);
  
    // store in the same shape YourPoints expects (selections.{GW}, captains.{GW}, chipsUsed.{GW})
    const payload = {
      leagueID,
      teamID,
      managerID: currentUser.uid,
      selections: { [currentGameweek]: team },
      captains: { [currentGameweek]: captainID || "" },
      chipsUsed: { [currentGameweek]: chipChoice || "None" },
      lastSavedAt: serverTimestamp(),
    };
  
    // merge so re-saving before the deadline just updates this GW snapshot
    await setDoc(snapRef, payload, { merge: true });
  };
  // Load league + team snapshot + lock + chipsUsed + transfersThisGW + maxFreeTransfers + startGW + teamMeta
  useEffect(() => {
    if (!currentUser || !leagueID || !teamID || !currentGameweek || !resolvedSeasonID) return;

    (async () => {
      try {
        setActiveSeasonID(resolvedSeasonID);

        // Fetch league, team, GW docs, and GW team snapshot
        const leagueRef = paths.league(leagueID);
        const teamRef = doc(paths.leagueTeams(leagueID), teamID);
        const gwRef = paths.gameweek(leagueID, currentGameweek);
        const gwTeamRef = doc(paths.gwTeams(leagueID, currentGameweek), teamID);

        const [, teamDoc, gwDoc, gwTeamDoc] = await Promise.all([
          getDoc(leagueRef),
          getDoc(teamRef),
          getDoc(gwRef),
          getDoc(gwTeamRef),
        ]);

        let loadedTeam = null;
        let carriedFrom = null;
        let loadedCaptain = "";
        let loadedChip = "None";
        let firestoreTransfers = 0;
        let normalizedmaxFreeTransfers = 1;
        // New: startGW extraction
        let loadedStartGW = "GW1";

        // For teamMeta fallback
        let squadMetaForGW = {};
        let fallbackSquadMeta = {};
        // --- New: gwTeamDoc squadMeta for currentGameweek ---
        let gwSnapshotSquadMeta = {};

        // New: baseBank for this GW
        let loadedBaseBank = null;

        // --- New prioritized baseBank/budget logic ---
        if (teamDoc.exists()) {
          const data = teamDoc.data();
          // Extract startGW
          loadedStartGW = data?.startGW || "GW1";
          setStartGW(loadedStartGW);

          // --- Absolute priority: currentBudget[currentGameweek] from teamDoc ---
          const currentBudgetForGW = data?.currentBudget?.[currentGameweek];
          if (typeof currentBudgetForGW === "number") {
            setBaseBank(currentBudgetForGW);
            setBudget(currentBudgetForGW);
            loadedBaseBank = currentBudgetForGW;
          } else {
            // fallback to bankStart on teamDoc
            const bankStartForGW = data?.bankStart?.[currentGameweek];
            if (typeof bankStartForGW === "number") {
              setBaseBank(bankStartForGW);
              setBudget(bankStartForGW);
              loadedBaseBank = bankStartForGW;
            } else if (gwTeamDoc.exists()) {
              // final fallback: gwTeamDoc.bankStart
              const gwTeamData = gwTeamDoc.data();
              const gwBankStart = gwTeamData?.bankStart?.[currentGameweek];
              if (typeof gwBankStart === "number") {
                setBaseBank(gwBankStart);
                setBudget(gwBankStart);
                loadedBaseBank = gwBankStart;
              } else {
                setBaseBank(null);
                setBudget(0);
              }
            }
          }

          let currentSelection = data?.selections?.[currentGameweek];
          // If no selections for currentGW, check previousGW and copy if exists
          let prevGWNum = null;
          let m = /^GW(\d+)$/.exec(currentGameweek);
          if (m) {
            prevGWNum = parseInt(m[1], 10) - 1;
          }
          let previousGW = prevGWNum && prevGWNum > 0 ? `GW${prevGWNum}` : null;
          if ((!currentSelection || Object.keys(currentSelection).length === 0) && previousGW && data?.selections?.[previousGW]) {
            currentSelection = data.selections[previousGW];
            carriedFrom = previousGW;
            loadedTeam = {};
            POSITIONS.forEach(pos => {
              loadedTeam[pos] = currentSelection[pos] || [];
            });
            // Copy captain and chip, but force chipChoice = "None"
            loadedCaptain = data?.captains?.[previousGW] || "";
            loadedChip = "None";
          }
          if (!loadedTeam) {
            // Normal load for this GW
            const teamWithPositions = {};
            POSITIONS.forEach(pos => {
              teamWithPositions[pos] = (currentSelection && currentSelection[pos]) || [];
            });
            loadedTeam = teamWithPositions;
            loadedCaptain = data?.captains?.[currentGameweek] || "";
            loadedChip = data?.chipsUsed?.[currentGameweek] || "None";
          }
          setTeam(loadedTeam);
          setOriginalTeam(loadedTeam);
          setChipsUsed(data?.chipsUsed || {});
          setCaptainID(loadedCaptain);
          setChipChoice(loadedChip);
          {
            const prevSel = previousGW ? data?.selections?.[previousGW] : null;
            const prevIds = POSITIONS.flatMap((p) => (Array.isArray(prevSel?.[p]) ? prevSel[p] : []));
            // Only meaningful if they had a team last week (not their first GW)
            setLastWeekIds(currentGameweek !== loadedStartGW && prevIds.length ? prevIds : null);
          }
          {
            const gwData = gwTeamDoc.exists() ? gwTeamDoc.data() : {};
            const hasPicks = POSITIONS.some((p) => (loadedTeam?.[p] || []).length > 0);
            const savedTs = gwData?.userSavedAt || gwData?.lastSavedAt;
            setSaveInfo(
              carriedFrom && hasPicks
                ? { kind: "carried", from: carriedFrom }
                : hasPicks
                ? { kind: "saved", at: savedTs?.toDate ? savedTs.toDate() : null }
                : { kind: "empty" }
            );
          }
          // Set transfersThisGW and savedTransfersBase from Firestore, else 0
          firestoreTransfers =
            typeof data.transfersMade?.[currentGameweek] === "number"
              ? data.transfersMade[currentGameweek]
              : 0;
          // Determine free transfers for this GW according to FPL logic:
          // Use transfersFree[currentGameweek] if present, else try to compute from previousGW, else fallback to 1
          let ftRaw = data?.transfersFree?.[currentGameweek];
          if (isUnlimitedFT(ftRaw)) {
            normalizedmaxFreeTransfers = 999;
          } else if (typeof ftRaw === "string" && /^\d+$/.test(ftRaw)) {
            normalizedmaxFreeTransfers = parseInt(ftRaw, 10);
          } else if (typeof ftRaw === "number") {
            normalizedmaxFreeTransfers = ftRaw;
          } else {
            // Not present for this GW, try to compute from previous GW
            let prevFree = undefined;
            let prevUsed = undefined;
            if (previousGW) {
              prevFree = data?.transfersFree?.[previousGW];
              prevUsed = data?.transfersMade?.[previousGW];
              // Normalize prevFree
              if (prevFree === undefined) {
                prevFree = 1;
              } else if (prevFree === "unlimited") {
                prevFree = "unlimited";
              } else if (typeof prevFree === "string" && /^\d+$/.test(prevFree)) {
                prevFree = parseInt(prevFree, 10);
              } else if (typeof prevFree === "number") {
                // ok
              } else {
                prevFree = 1;
              }
              // Normalize prevUsed
              if (typeof prevUsed !== "number") {
                prevUsed = 0;
              }
              const cap = leagueConfig?.maxFreeTransfers ?? 2;
              normalizedmaxFreeTransfers = calculateNextmaxFreeTransfers(prevFree, prevUsed, cap);
            } else {
              normalizedmaxFreeTransfers = 1;
            }
          }
          // New: squadMeta for this GW, fallback to previousGW if not present
          squadMetaForGW = data?.squadMeta?.[currentGameweek] || {};
          if (
            (!squadMetaForGW || Object.keys(squadMetaForGW).length === 0) &&
            previousGW &&
            data?.squadMeta?.[previousGW]
          ) {
            fallbackSquadMeta = data.squadMeta[previousGW];
          }
          // Override logic for startGW
          if (currentGameweek === loadedStartGW) {
            setTransfersThisGW(0);
            setSavedTransfersBase(0);
            setTransferPenalty(0);
            setmaxFreeTransfers(999);
          } else {
            setTransfersThisGW(firestoreTransfers);
            setSavedTransfersBase(firestoreTransfers);
            setmaxFreeTransfers(normalizedmaxFreeTransfers);
          }
        } else {
          // If no teamDoc, treat as startGW
          setBaseBank(null);
          setBudget(0);
          if (currentGameweek === "GW1") {
            setTransfersThisGW(0);
            setSavedTransfersBase(0);
            setTransferPenalty(0);
            setmaxFreeTransfers(999);
            setStartGW("GW1");
          } else {
            setmaxFreeTransfers(1);
            setTransfersThisGW(0);
            setSavedTransfersBase(0);
          }
        }

        // --- Extract squadMeta from GW snapshot (gwTeamDoc) ---
        if (gwTeamDoc.exists()) {
          const gwTeamData = gwTeamDoc.data();
          // squadMeta is expected to be an object: { [GW]: { ... } }
          if (
            gwTeamData &&
            gwTeamData.squadMeta &&
            typeof gwTeamData.squadMeta === "object" &&
            gwTeamData.squadMeta[currentGameweek] &&
            typeof gwTeamData.squadMeta[currentGameweek] === "object"
          ) {
            gwSnapshotSquadMeta = gwTeamData.squadMeta[currentGameweek];
          }
        }

        // --- Merge squadMeta: GW snapshot takes priority, then teamDoc, then fallback ---
        // Merge all sources into a single object by player ID
        const mergedMeta = {};
        // Helper to flatten squadMeta (which may be by position, playerId, or index)
        function flattenSquadMeta(meta) {
          if (!meta || typeof meta !== "object") return {};

          // If already keyed by playerId (buyPrice/buyGW present), just return
          const looksFlat = Object.values(meta).every(
            v => v && typeof v === "object" && ("buyPrice" in v || "buyGW" in v)
          );
          if (looksFlat) return meta;

          const flat = {};
          // Loop across each position (DEF/MID/FWD) or other keys
          Object.keys(meta).forEach(pos => {
            const posMeta = meta[pos];
            if (posMeta && typeof posMeta === "object") {
              Object.keys(posMeta).forEach(innerKey => {
                const entry = posMeta[innerKey];
                if (entry && typeof entry === "object") {
                  // Prefer explicit id field if present, otherwise use innerKey
                  const pid = entry.id || innerKey;
                  if (!pid) return;
                  flat[pid] = {
                    ...entry,
                    // Preserve buyPrice/buyGW if available
                    buyPrice: typeof entry.buyPrice === "number" ? entry.buyPrice : undefined,
                    buyGW: entry.buyGW || undefined,
                  };
                }
              });
            }
          });
          return flat;
        }
        // Priority: GW snapshot > teamDoc squadMetaForGW > fallbackSquadMeta
        Object.assign(
          mergedMeta,
          flattenSquadMeta(fallbackSquadMeta),
          flattenSquadMeta(squadMetaForGW),
          flattenSquadMeta(gwSnapshotSquadMeta)
        );
        setTeamMeta(mergedMeta);
        setLoadedTick((t) => t + 1); // snapshot the loaded state for unsaved-change detection

        if (gwDoc.exists()) {
          const d = gwDoc.data();
          if (d?.deadline?.toDate) {
            setIsLocked(new Date() > d.deadline.toDate());
          }
        }
      } catch (err) {
        console.error("Error loading league/team/gameweek:", err);
        await logError({
          leagueID,
          userID: currentUser?.uid || "unknown",
          action: "PickTeam_LoadLeagueData",
          payload: { leagueID, teamID, currentGameweek },
          error: err
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, leagueID, teamID, POSITIONS, currentGameweek, resolvedSeasonID]);

  // Load players directly by leagueID, gated on resolved season.
  // Also re-fetches when selectedSeasonID changes (user switches season in modal).
  useEffect(() => {
    if (!currentUser || !leagueID || !resolvedSeasonID) return;
    const seasonToFetch = selectedSeasonID || resolvedSeasonID;

    (async () => {
      try {
        setActiveSeasonID(seasonToFetch);
        const playersRef = paths.leaguePlayers(leagueID, seasonToFetch);
        const snapshot = await getDocs(query(playersRef));
        const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setPlayers(data);
      } catch (err) {
        await logError({
          leagueID,
          userID: currentUser?.uid || "unknown",
          action: "PickTeam_FetchPlayers",
          payload: { leagueID, seasonToFetch },
          error: err
        });
      }
    })();
  }, [currentUser, leagueID, resolvedSeasonID, selectedSeasonID]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!leagueID || !resolvedSeasonID || !currentGameweek || !currentUser) return;

    (async () => {
      try {
        const availRef = doc(
          db,
          "leagues", leagueID,
          "seasons", resolvedSeasonID,
          "gameweeks", currentGameweek,
          "availability", "responses"
        );
        const availSnap = await getDoc(availRef);
        if (!availSnap.exists()) return;
        const responses = availSnap.data()?.responses || {};

        const availablePlayerIDSet = new Set();

        await Promise.all(
          Object.entries(responses)
            .filter(([, status]) => status === "available")
            .map(async ([key]) => {
              // If the key matches a player ID directly (unlinked player response), add it straight away
              const isDirectPlayerID = players.some(p => p.id === key);
              if (isDirectPlayerID) {
                availablePlayerIDSet.add(key);
                return;
              }
              // Otherwise treat as a userUID and resolve their linkedPlayerID
              try {
                const userSnap = await getDoc(doc(db, "users", key));
                if (userSnap.exists()) {
                  const linkedPlayerID = userSnap.data()?.linkedPlayerIDs?.[leagueID];
                  if (linkedPlayerID) availablePlayerIDSet.add(linkedPlayerID);
                }
              } catch (_) {}
            })
        );

        setAvailablePlayerIDs(availablePlayerIDSet);
      } catch (err) {
        console.error("PickTeam: failed to fetch availability", err);
      }
    })();
  }, [leagueID, resolvedSeasonID, currentGameweek, currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Helper: get effective price for a player (uses teamMeta, buyPrice, etc) ---
  // mode: "current" (default) shows current price, "buy" will use buyPrice if available
  const getEffectivePrice = (player, mode = "current") => {
    if (!player) return 0;
    const meta = teamMeta[player.id];
    if (mode === "buy" && meta && typeof meta.buyPrice === "number") {
      return meta.buyPrice;
    }
    // current price for display and validation
    return player.priceHistory?.[currentGameweek] ?? player.price;
  };


  // Helper: For UI display: get retained buy price (original buy price for retained players)
  const getRetainedBuyPrice = (player, id) => {
    if (!player) return 0;
    // 1. Check merged teamMeta (which now includes GW snapshot data)
    if (teamMeta && teamMeta[id] && typeof teamMeta[id].buyPrice === "number") {
      return teamMeta[id].buyPrice;
    }
    // 2. Fallback to startGW priceHistory
    if (player.priceHistory && startGW && player.priceHistory[startGW] !== undefined) {
      return player.priceHistory[startGW];
    }
    // 3. Fallback to base price
    return player.price;
  };

  // --- Validation / budget ---
  useEffect(() => {
    // --- Budget calculation logic (simplified to always start from baseBank for non-startGW) ---
    // Helper: get all player IDs for a team object
    const getTeamIds = (teamObj) =>
      POSITIONS.flatMap((pos) => Array.isArray(teamObj?.[pos]) ? teamObj[pos] : []);

    const leagueBudget = leagueConfig.budget ?? 24.0;
    let calcBudget = 0;

    if (currentGameweek === startGW) {
      // For startGW, budget is leagueBudget - sum(buyPrices of current selections)
      const teamIds = getTeamIds(team);
      let totalBuy = 0;
      for (const id of teamIds) {
        const player = byId.get(id);
        if (player) {
          // Use getRetainedBuyPrice to get buyPrice of selection
          const buyPrice = getRetainedBuyPrice(player, id);
          totalBuy += buyPrice;
        }
      }
      calcBudget = leagueBudget - totalBuy;
    } else {
      // For later GWs, always start from baseBank (bankStart[currentGameweek]) if present
      if (typeof baseBank === "number") {
        // Always set calcBudget to baseBank first
        calcBudget = baseBank;
        // Only apply adjustment if user has interacted
        if (hasInteracted) {
          const getIds = getTeamIds;
          const origIds = new Set(getIds(originalTeam || {}));
          const currIds = new Set(getIds(team));
          let adjustment = 0;
          // Subtract currentPrice for each new player
          for (const id of currIds) {
            if (!origIds.has(id)) {
              const player = byId.get(id);
              if (player) {
                const currentPrice = player.priceHistory?.[currentGameweek] ?? player.price;
                adjustment -= currentPrice;
              }
            }
          }
          // Add back currentPrice for each deselected player
          for (const id of origIds) {
            if (!currIds.has(id)) {
              const player = byId.get(id);
              if (player) {
                const currentPrice = player.priceHistory?.[currentGameweek] ?? player.price;
                adjustment += currentPrice;
              }
            }
          }
          calcBudget = baseBank + adjustment;
        }
      } else {
        // If no baseBank, fallback to 0
        calcBudget = 0;
      }
    }
    // Always set budget immediately after calculation
    setBudget(calcBudget);

    // --- Keep existing validation logic below ---
    let chipError = "";
    if (chipChoice && chipChoice !== "None") {
      const used = Object.values(chipsUsed || {}).includes(chipChoice);
      if (used && chipsUsed[currentGameweek] !== chipChoice) {
        chipError = `You have already used "${chipChoice}" earlier this season.`;
      }
    }

    // Use selectedPlayers for captain validation
    const chosen = new Set(selectedPlayers.map((p) => p.id));
    const captainValid = captainID && chosen.has(captainID);
    // Allow small floating point rounding errors (e.g., -0.0)
    const budgetOk = calcBudget >= -0.05;

    // Transfer penalty: Use selectedLeague.transferPenaltyPoints if available, else leagueConfig.transferPenalty, else 5
    let penaltyPerTransfer = 5;
    if (selectedLeague && typeof selectedLeague.transferPenaltyPoints === "number") {
      penaltyPerTransfer = selectedLeague.transferPenaltyPoints;
    } else if (typeof leagueConfig.transferPenalty === "number") {
      penaltyPerTransfer = leagueConfig.transferPenalty;
    }
    // Special: If currentGameweek === startGW, force penalty 0, else normal logic
    if (currentGameweek === startGW) {
      setTransferPenalty(0);
    } else {
      // maxFreeTransfers is normalized to 999 for "unlimited", else a number
      let normalizedFT = maxFreeTransfers;
      if (typeof normalizedFT !== "number") {
        if (normalizedFT === "unlimited") normalizedFT = 999;
        else if (typeof normalizedFT === "string" && /^\d+$/.test(normalizedFT)) normalizedFT = parseInt(normalizedFT, 10);
        else normalizedFT = 1;
      }
      const calculatedTransferPenalty =
        transfersThisGW > normalizedFT
          ? (transfersThisGW - normalizedFT) * penaltyPerTransfer
          : 0;
      setTransferPenalty(calculatedTransferPenalty);
    }

    if (!hasInteracted) {
      setErrorMsg("");
      let lines = [];
      const lt = selectedLeague?.leagueType?.toLowerCase?.() || "";
    
      // Define per-league line rules
      if (lt.includes("5")) {
        lines = ["Rules:", "3 players total", "max 2 per position"];
      } else if (lt.includes("7")) {
        lines = ["Rules:", "4 players total", "min 1, max 2 per position"];
      } else if (lt.includes("9") || lt.includes("11")) {
        lines = ["Rules:", "5 players total", "min 1, max 3 per position"];
      } else {
        lines = ["Rules:", "check squad rules"];
      }
    
      // Join with newline characters so your rendering logic can split it cleanly
      setTeamError(lines.join("\n"));
      return;
    }

    // Don't check team structure validity here, only budget/captain/chip errors.
    if (!budgetOk) setErrorMsg("Over budget");
    else if (!captainValid) setErrorMsg("Select captain");
    else if (chipError) setErrorMsg(chipError);
    else setErrorMsg("");
    // Remove duplicate setBudget(calcBudget) here (now always set above)
    // setBudget(calcBudget);
  }, [
    team,
    byId,
    chipChoice,
    chipsUsed,
    captainID,
    POSITIONS,
    selectedLeague,
    leagueConfig.budget,
    leagueConfig.transferPenalty,
    leagueConfig.positions,
    leagueConfig.chips,
    transfersThisGW,
    hasInteracted,
    maxFreeTransfers,
    selectedPlayers,
    currentGameweek,
    startGW,
    teamMeta,
    originalTeam,
    baseBank, // ensure baseBank is explicitly in deps
  ]);

  // Modified toggleInSlot: now respects dynamic maxPerPos from positionRules
  const toggleInSlot = (playerId, slot, checked) => {
    const player = byId.get(playerId);
    if (!player) return;
    setTeam((prev) => {
      const next = { ...prev };
      if (checked) {
        // Always use positionRules from squadConfig
        const maxPerPos = positionRules?.[slot]?.max ?? 1;
        const minPerPos = positionRules?.[slot]?.min ?? 0;
        const existing = next[slot] || [];
        if (existing.length < maxPerPos && !existing.includes(playerId)) {
          next[slot] = [...existing, playerId];
        }
      } else {
        // Always allow removal. Min-per-position feasibility is handled when adding players.
        const existing = next[slot] || [];
        next[slot] = existing.filter(id => id !== playerId);
      }
      return next;
    });
    setHasInteracted(true);
  };

  const isPlayerSelectedElsewhere = (playerID, slot) =>
    POSITIONS.some((p) => p !== slot && Array.isArray(team[p]) && team[p].includes(playerID));

  const wouldBreakCompositionIfAdded = (player, slot) => {
    const rules = positionRules || {};
    const maxPlayers = leagueConfig.squadConfig?.maxPlayers ?? 3;

    // Clone team and add this player temporarily
    const next = {};
    POSITIONS.forEach((pos) => {
      next[pos] = [...(team[pos] || [])];
    });
    if (!next[slot].includes(player.id)) {
      next[slot].push(player.id);
    }

    // Count how many players are selected per position label (DEF/MID/FWD)
    const counts = {};
    POSITIONS.forEach((pos) => {
      next[pos].forEach((id) => {
        const p = byId.get(id);
        if (p && p.position) {
          counts[p.position] = (counts[p.position] || 0) + 1;
        }
      });
    });

    const totalSelected = Object.values(counts).reduce((sum, v) => sum + v, 0);

    // Hard cap: cannot exceed maxPlayers
    if (totalSelected > maxPlayers) return true;

    // Build the set of positions we care about (from POSITIONS and rules)
    const allPositions = Array.from(
      new Set([...POSITIONS, ...Object.keys(rules || {})])
    );

    let requiredExtra = 0; // minimum extra players needed to reach all mins
    let availableSlots = 0; // maximum extra players we can still add without breaching maxes

    const remainingSlots = maxPlayers - totalSelected;

    for (const pos of allPositions) {
      const rule = rules[pos] || {};
      const min = typeof rule.min === "number" ? rule.min : 0;
      const max = typeof rule.max === "number" ? rule.max : maxPlayers;
      const current = counts[pos] || 0;

      // If we already exceed max for this position, it's invalid.
      if (current > max) {
        return true;
      }

      // How many we *must* add later to reach the minimum for this position
      const need = Math.max(0, min - current);
      requiredExtra += need;

      // How many we *could* still add to this position without exceeding max
      const cap = Math.max(0, max - current);
      availableSlots += cap;
    }

    // If we don't have enough remaining slots to ever reach all mins, it's invalid.
    if (requiredExtra > remainingSlots) {
      return true;
    }

    // If even filling all positions up to their max can't absorb the remaining
    // slots, then we can't ever hit exactly maxPlayers without breaking a max.
    if (remainingSlots > availableSlots) {
      return true;
    }

    // Otherwise, there exists at least one valid final squad configuration
    // consistent with these rules, so this add is allowed.
    return false;
  };

  // Firestore write promises never settle while offline — cap them so the
  // "Saving squad…" overlay can't hang forever.
  const SAVE_TIMEOUT_MS = 20000;
  const withTimeout = (promise, label) =>
    Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error(`Timed out after ${SAVE_TIMEOUT_MS / 1000}s (${label})`)), SAVE_TIMEOUT_MS)
      ),
    ]);
  const SAVE_FAILED_MSG = "Your team was NOT saved. Check your connection and try again.";

  const handleSaveSquad = async () => {
    try {
      // --- Squad size validation ---
      const { minPlayers, maxPlayers } = leagueConfig.squadConfig || {};
      const totalSelectedCount = POSITIONS.reduce((sum,pos)=>sum+(team[pos]?.length||0),0);
      if (minPlayers && totalSelectedCount < minPlayers) {
        showToast(`You must select ${minPlayers} players.`, "error");
        return;
      }
      if (maxPlayers && totalSelectedCount > maxPlayers) {
        showToast(`You must select ${maxPlayers} players.`, "error");
        return;
      }
      // Per-position min/max validation
      for (const pos of POSITIONS) {
        const rules = positionRules[pos] || {};
        const count = team[pos]?.length || 0;
        if (rules.min && count < rules.min) {
          showToast(`You must select ${rules.min} ${pos}.`, "error");
          return;
        }
        if (rules.max && count > rules.max) {
          showToast(`You must select ${rules.max} ${pos}.`, "error");
          return;
        }
      }
      // Only check budget, captain, and chip constraints
      const chosen = new Set(selectedPlayers.map((p) => p.id));
      // Captain validation: only valid if captainID is set and among currently chosen players
      const captainValid = captainID ? chosen.has(captainID) : false;
      const budgetOk = budget >= -0.05;

      let chipOk = true;
      if (chipChoice && chipChoice !== "None") {
        const used = Object.values(chipsUsed || {}).includes(chipChoice);
        chipOk = !(used && chipsUsed[currentGameweek] !== chipChoice);
      }

      // Do not setTeamError here

      if (!captainValid || !budgetOk || !chipOk) {
        showToast("Your team is invalid. Fix errors and try again.", "error");
        return;
      }
      if (!teamID || !currentGameweek) {
        showToast("Missing gameweek or team ID.", "error");
        return;
      }

      setIsSaving(true);
      try {
        // 1) write to the live team doc
        try {
          // Normalize maxFreeTransfers for writing
          let transfersFreeVal = maxFreeTransfers;
          if (currentGameweek === startGW) {
            transfersFreeVal = "unlimited";
          } else if (isUnlimitedFT(maxFreeTransfers)) {
            // Persist the canonical string, never the 999 in-memory sentinel.
            transfersFreeVal = "unlimited";
          } else if (typeof maxFreeTransfers === "number") {
            transfersFreeVal = maxFreeTransfers;
          } else if (typeof maxFreeTransfers === "string" && /^\d+$/.test(maxFreeTransfers)) {
            transfersFreeVal = parseInt(maxFreeTransfers, 10);
          } else {
            transfersFreeVal = 1;
          }
          // Set transfersMadeVal: 0 if currentGameweek === startGW, else Number(transfersThisGW)
          let transfersMadeVal = currentGameweek === startGW ? 0 : Number(transfersThisGW);
          // Save current budget for this GW in currentBudget.{currentGameweek}
          const liveTeamRef = doc(paths.leagueTeams(leagueID), teamID);
          await withTimeout(updateDoc(liveTeamRef, {
            userSavedAt: serverTimestamp(),
            [`selections.${currentGameweek}`]: team,
            [`captains.${currentGameweek}`]: captainID,
            [`chipsUsed.${currentGameweek}`]: chipChoice || "None",
            [`transfersMade.${currentGameweek}`]: transfersMadeVal,
            [`transfersFree.${currentGameweek}`]:
              transfersFreeVal === "unlimited" ? "unlimited" : Number(transfersFreeVal),
            [`squadMeta.${currentGameweek}`]: team,
            // Ensure one decimal place for budget
            [`currentBudget.${currentGameweek}`]: Number(budget.toFixed(1)),
          }), "team doc");
        } catch (e) {
          console.error("Failed to write live team doc:", e);
          setIsSaving(false);
          await logError({
            leagueID,
            userID: currentUser?.uid || "unknown",
            action: "PickTeam_SaveSquad_liveTeamDoc",
            payload: { teamID, currentGameweek, team, captainID, chipChoice },
            error: e,
          });
          showToast(SAVE_FAILED_MSG, "error", false, 8000);
          return;
        }

        // 2) write the frozen GW snapshot (merged: selections, captains, chipsUsed, squadMeta, etc.)
        try {
          // --- Build squadMeta for this GW, with proper reset handling and overwrite logic ---
          // Helper: flatten squadMeta by position or by playerId (supports both array and object formats)
          const flattenMetaByPos = (byPos) => {
            const flat = {};
            if (!byPos || typeof byPos !== "object") return flat;
            Object.keys(byPos).forEach((pos) => {
              const bucket = byPos[pos];
              if (Array.isArray(bucket)) {
                bucket.forEach((entry) => {
                  if (entry && entry.id) {
                    flat[entry.id] = {
                      buyGW: entry.buyGW,
                      buyPrice: entry.buyPrice,
                    };
                  }
                });
              } else if (typeof bucket === "object") {
                Object.keys(bucket).forEach((pid) => {
                  const entry = bucket[pid];
                  if (entry && (entry.buyGW || typeof entry.buyPrice === "number")) {
                    flat[pid] = { buyGW: entry.buyGW, buyPrice: entry.buyPrice };
                  }
                });
              }
            });
            return flat;
          };

          // --- Load baseline meta (from GW snapshot or previous GW snapshot) ---
          let baselineMeta = {};
          try {
            const curSnapRef = doc(paths.gwTeams(leagueID, currentGameweek), teamID);
            const curSnap = await getDoc(curSnapRef);
            if (curSnap.exists()) {
              const curData = curSnap.data();
              if (curData?.squadMeta?.[currentGameweek]) {
                baselineMeta = flattenMetaByPos(curData.squadMeta[currentGameweek]);
              }
            }
            // Fallback to previous GW if nothing found
            if (!baselineMeta || Object.keys(baselineMeta).length === 0) {
              const mPrev = /^GW(\d+)$/.exec(currentGameweek);
              const prevGW =
                mPrev && parseInt(mPrev[1], 10) > 1 ? `GW${parseInt(mPrev[1], 10) - 1}` : null;
              if (prevGW) {
                const prevSnapRef = doc(paths.gwTeams(leagueID, prevGW), teamID);
                const prevSnap = await getDoc(prevSnapRef);
                if (prevSnap.exists()) {
                  const prevData = prevSnap.data();
                  if (prevData?.squadMeta?.[prevGW]) {
                    baselineMeta = flattenMetaByPos(prevData.squadMeta[prevGW]);
                  }
                }
              }
            }
          } catch (err) {
            console.error("Unable to load baseline squadMeta:", err);
          }

          // --- Determine if reset was used: team matches originalTeam and !hasInteracted ---
          const getTeamIds = (teamObj) => POSITIONS.flatMap((pos) => Array.isArray(teamObj?.[pos]) ? teamObj[pos] : []);
          const origIdsArr = getTeamIds(originalTeam || {});
          const currIdsArr = getTeamIds(team);
          const resetWasUsed =
            !hasInteracted &&
            origIdsArr.length === currIdsArr.length &&
            origIdsArr.every((id) => currIdsArr.includes(id)) &&
            currIdsArr.every((id) => origIdsArr.includes(id));

          // --- Load previous GW squadMeta snapshot for comparison ---
          let prevMeta = {};
          try {
            const mPrev = /^GW(\d+)$/.exec(currentGameweek);
            const prevGW =
              mPrev && parseInt(mPrev[1], 10) > 1 ? `GW${parseInt(mPrev[1], 10) - 1}` : null;
            if (prevGW) {
              const prevSnapRef = doc(paths.gwTeams(leagueID, prevGW), teamID);
              const prevSnap = await getDoc(prevSnapRef);
              if (prevSnap.exists()) {
                const prevData = prevSnap.data();
                if (prevData?.squadMeta?.[prevGW]) {
                  prevMeta = flattenMetaByPos(prevData.squadMeta[prevGW]);
                }
              }
            }
          } catch (err) {
            console.error("Unable to load prevMeta squadMeta:", err);
          }

          // --- Compose outgoing squadMetaUpdate ---
          const squadMetaUpdate = {};
          ["DEF", "MID", "FWD"].forEach((pos) => {
            const posSelections = team[pos] || [];
            if (!squadMetaUpdate[pos]) squadMetaUpdate[pos] = {};
            posSelections.forEach((playerID) => {
              // If reset was used, use baselineMeta exactly (for buyPrice/buyGW), do NOT assign new buyGW/currentPrice for retained players
              if (resetWasUsed && baselineMeta[playerID]) {
                squadMetaUpdate[pos][playerID] = {
                  buyGW: baselineMeta[playerID].buyGW,
                  buyPrice: baselineMeta[playerID].buyPrice,
                };
              } else {
                // New logic: prefer baselineMeta, else prevMeta, else assign new buyGW/currentPrice
                const previous = prevMeta[playerID];
                const keep = baselineMeta[playerID] || previous;
                if (keep) {
                  squadMetaUpdate[pos][playerID] = {
                    buyGW: keep.buyGW,
                    buyPrice: keep.buyPrice,
                  };
                } else {
                  const p = byId.get(playerID);
                  const currentPrice =
                    (p && (p.priceHistory?.[currentGameweek] ?? p.currentPrice ?? p.price ?? 0)) || 0;
                  squadMetaUpdate[pos][playerID] = {
                    buyGW: currentGameweek,
                    buyPrice: currentPrice,
                  };
                }
              }
            });
          });

          // --- Overwrite squadMeta[currentGameweek] in the snapshot doc ---
          const gwSnapshotRef = doc(paths.gwTeams(leagueID, currentGameweek), teamID);
          // Compose payload, but do NOT merge squadMeta[currentGameweek]
          const payload = {
            leagueID,
            teamID,
            managerID: currentUser.uid,
            selections: { [currentGameweek]: team },
            captains: { [currentGameweek]: captainID || "" },
            chipsUsed: { [currentGameweek]: chipChoice || "None" },
            lastSavedAt: serverTimestamp(),
          };
          // Use updateDoc to overwrite squadMeta[currentGameweek] field explicitly
          // Ensure buyPrice and currentPrice fields are saved with one decimal place
          const roundedSquadMetaUpdate = {};
          Object.keys(squadMetaUpdate).forEach((pos) => {
            roundedSquadMetaUpdate[pos] = {};
            Object.keys(squadMetaUpdate[pos] || {}).forEach((pid) => {
              const meta = squadMetaUpdate[pos][pid];
              roundedSquadMetaUpdate[pos][pid] = {
                ...meta,
                ...(meta && typeof meta.buyPrice === "number"
                  ? { buyPrice: Number(meta.buyPrice.toFixed(1)) }
                  : {}),
              };
            });
          });
          await withTimeout(updateDoc(gwSnapshotRef, {
            ...payload,
            userSavedAt: serverTimestamp(),
            [`squadMeta.${currentGameweek}`]: roundedSquadMetaUpdate,
            ...(typeof budget === "number" && { [`currentBudget.${currentGameweek}`]: Number(budget.toFixed(1)) }),
          }), "GW snapshot");
        } catch (e) {
          // NOTE: the team doc above is already written at this point — this is the
          // half-save case. Logged so it's visible; the atomic-batch fix removes it.
          console.error("Failed to write GW snapshot:", e);
          setIsSaving(false);
          await logError({
            leagueID,
            userID: currentUser?.uid || "unknown",
            action: "PickTeam_SaveSquad_gwSnapshot_HALF_SAVE",
            payload: { teamID, currentGameweek, team, captainID, chipChoice },
            error: e,
          });
          showToast(SAVE_FAILED_MSG, "error", false, 8000);
          return;
        }
        // Analytics: explicit completion signal (do NOT fire on autosave)
        try {
          const totalSelectedCount = POSITIONS.reduce(
            (sum, pos) => sum + ((team?.[pos] || []).length || 0),
            0
          );

          await logEvent({
            eventType: "squad_saved",
            leagueID,
            userID: currentUser?.uid || "anon",
            userName: userName || "",
            role: userRole || "",
            source: "pick_team_save",
            metadata: {
              gameweekID: currentGameweek || "",
              teamID: teamID || "",
              playersSelected: totalSelectedCount,
              captainSet: Boolean(captainID),
              chipChoice: chipChoice || "None",
              transfersThisGW: typeof transfersThisGW === "number" ? transfersThisGW : 0,
              budgetRemaining: typeof budget === "number" ? Number(budget.toFixed(1)) : null,
              isStartGW: Boolean(startGW && currentGameweek === startGW),
            },
          });
        } catch (e) {
          // Never block save flow on analytics
          console.warn("logEvent squad_saved failed:", e);
        }
        // 3) navigate only after both succeed
        setIsSaving(false);
        setNavGuard(null);
        {
          const capName = selectedPlayers.find((p) => p.id === captainID)?.name;
          showToast(`✓ ${currentGameweek} team saved${capName ? ` · Captain: ${capName}` : ""}`, "success", false, 6000);
        }
        navigate(`/team/${leagueID}`);
      } catch (e) {
        setIsSaving(false);
        console.error("Failed to save squad:", e);
        await logError({
          leagueID,
          userID: currentUser?.uid || "unknown",
          action: "PickTeam_SaveSquad",
          payload: { team, captainID, chipChoice, budget },
          error: e
        });
        showToast(SAVE_FAILED_MSG, "error", false, 8000);
      }
    } catch (e) {
      setIsSaving(false);
      console.error("Failed to save squad:", e);
      await logError({
        leagueID,
        userID: currentUser?.uid || "unknown",
        action: "PickTeam_SaveSquad",
        payload: { team, captainID, chipChoice, budget },
        error: e
      });
      showToast(SAVE_FAILED_MSG, "error", false, 8000);
    }
  };

  const saveDisabled = Boolean(errorMsg) || isLocked || isSaving;

  // ---- Unsaved-change detection -------------------------------------------
  // Snapshot of the state as loaded from Firestore; everything is diffed against it.
  useEffect(() => {
    if (!loadedTick) return;
    setSavedState({
      team,
      originalTeam,
      captainID,
      chipChoice,
      chipsUsed,
      baseBank,
      savedTransfersBase,
      transfersThisGW,
      maxFreeTransfers,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedTick]);

  const flatIds = (t) => POSITIONS.flatMap((p) => (Array.isArray(t?.[p]) ? t[p] : []));
  const savedIds = savedState ? flatIds(savedState.team) : [];
  const currentIds = flatIds(team);
  const playersIn = savedState ? currentIds.filter((id) => !savedIds.includes(id)) : [];
  const playersOut = savedState ? savedIds.filter((id) => !currentIds.includes(id)) : [];
  const captainChanged = !!savedState && (captainID || "") !== (savedState.captainID || "");
  const chipChanged = !!savedState && (chipChoice || "None") !== (savedState.chipChoice || "None");
  const isDirty =
    !!savedState && (playersIn.length > 0 || playersOut.length > 0 || captainChanged || chipChanged);
  // NEW = every player brought in this gameweek (saved or not), vs last week's team.
  // First GW / no previous team: fall back to players added since the last save.
  const newPlayerIDs = new Set(
    lastWeekIds ? currentIds.filter((id) => !lastWeekIds.includes(id)) : playersIn
  );

  // Block in-app navigation (nav bar / menu) and tab close while there are unsaved changes
  useEffect(() => {
    if (!isDirty || isSaving) {
      setNavGuard(null);
      return undefined;
    }
    setNavGuard((proceed) => setPendingNav(() => proceed));
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      setNavGuard(null);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [isDirty, isSaving]);

  const handleUndoChanges = () => {
    if (!savedState) return;
    setTeam(savedState.team);
    setOriginalTeam(savedState.originalTeam);
    setCaptainID(savedState.captainID);
    setChipChoice(savedState.chipChoice);
    setChipsUsed(savedState.chipsUsed);
    setBaseBank(savedState.baseBank);
    setSavedTransfersBase(savedState.savedTransfersBase);
    setTransfersThisGW(savedState.transfersThisGW);
    setmaxFreeTransfers(savedState.maxFreeTransfers);
    setHasInteracted(false);
  };

  // Helper to calculate next free transfers according to rollover rules
  const calculateNextmaxFreeTransfers = (prevFree, used, cap = 2) => {
    if (prevFree === "unlimited") return 1;
    const prev = typeof prevFree === "number" ? prevFree : 1;
    const remaining = Math.max(prev - used, 0);
    const next = remaining + 1;
    return Math.min(next, cap);
  };

  // Reset to last week's selection (from snapshot if exists, else fallback)
  const handleResetToLastWeek = async () => {
    // Parse previous gameweek
    if (!currentGameweek || !leagueID || !teamID) return;
    let prevGWNum = null;
    let m = /^GW(\d+)$/.exec(currentGameweek);
    if (m) {
      prevGWNum = parseInt(m[1], 10) - 1;
    }
    let previousGW = prevGWNum && prevGWNum > 0 ? `GW${prevGWNum}` : null;
    let foundSnapshot = false;
    // Helper to get the correct bankStart for the current gameweek from snapshot
    const getSnapshotBankStart = async () => {
      try {
        // Always get the latest /leagues/{leagueID}/gameweeks/{currentGameweek}/teams/{teamID} doc
        const gwTeamRef = doc(paths.gwTeams(leagueID, currentGameweek), teamID);
        const gwTeamDoc = await getDoc(gwTeamRef);
        if (gwTeamDoc.exists()) {
          const gwTeamData = gwTeamDoc.data();
          if (
            gwTeamData &&
            gwTeamData.bankStart &&
            typeof gwTeamData.bankStart[currentGameweek] === "number"
          ) {
            return gwTeamData.bankStart[currentGameweek];
          }
        }
      } catch (err) {
        console.error("Failed to fetch GW snapshot for budget:", err);
      }
      return undefined;
    };
    let snapshotBankStart = undefined;
    if (previousGW) {
      try {
        // Try to fetch snapshot from /leagues/{leagueID}/gameweeks/{previousGW}/teams/{teamID}
        const snapRef = doc(paths.gwTeams(leagueID, previousGW), teamID);
        const snapDoc = await getDoc(snapRef);
        if (snapDoc.exists()) {
          const data = snapDoc.data();
          const selections = data?.selections?.[previousGW];
          const captain = data?.captains?.[previousGW] || "";
          // Only reset if selections exist
          if (selections && typeof selections === "object") {
            const teamWithPositions = {};
            POSITIONS.forEach(pos => {
              teamWithPositions[pos] = selections[pos] || [];
            });
            // Retrieve correct budget for current gameweek snapshot
            snapshotBankStart = await getSnapshotBankStart();
            setHasInteracted(false);
            setOriginalTeam(teamWithPositions);
            setBudget(
              typeof snapshotBankStart === "number"
                ? snapshotBankStart
                : baseBank ?? 0
            );
            setBaseBank(
              typeof snapshotBankStart === "number"
                ? snapshotBankStart
                : baseBank ?? 0
            );
            setTeam(teamWithPositions);
            setCaptainID(captain);
            setChipChoice("None");
            // Explicitly reset transfer-related state to defaults
            setmaxFreeTransfers(maxFreeTransfers);
            setTransfersThisGW(0);
            setSavedTransfersBase(0);
            setTransferPenalty(0);
            foundSnapshot = true;
          }
        }
      } catch (err) {
        console.error("Failed to fetch previous GW team snapshot:", err);
      }
    }
    // Fallback to originalTeam if no snapshot found
    if (!foundSnapshot && originalTeam) {
      // Try to get snapshot bankStart for currentGameweek for fallback as well
      snapshotBankStart = await getSnapshotBankStart();
      setHasInteracted(false);
      setOriginalTeam(originalTeam);
      setBudget(
        typeof snapshotBankStart === "number"
          ? snapshotBankStart
          : baseBank ?? 0
      );
      setBaseBank(
        typeof snapshotBankStart === "number"
          ? snapshotBankStart
          : baseBank ?? 0
      );
      setTeam(originalTeam);
      setChipChoice("None");
      setmaxFreeTransfers(maxFreeTransfers);
      setTransfersThisGW(0);
      setSavedTransfersBase(0);
      setTransferPenalty(0);
      setCaptainID(captainID);
    }
  };

  // Safeguard: if captainID is set but not among selectedPlayers, clear it
  useEffect(() => {
    if (captainID && !selectedPlayers.find(p => p.id === captainID)) {
      setCaptainID("");
    }
  }, [team, captainID, selectedPlayers]);

  // Guarded setActiveSeasonID from selectedLeague.currentSeasonID if present elsewhere
  useEffect(() => {
    if (
      selectedLeague &&
      typeof selectedLeague.currentSeasonID === "string" &&
      selectedLeague.currentSeasonID.trim() !== ""
    ) {
      const seasonID = selectedLeague.currentSeasonID.trim();
      setResolvedSeasonID(seasonID);
      setActiveSeasonID(seasonID);
    }
  }, [selectedLeague]);


  // Wrap setCaptainID and setChipChoice to also set hasInteracted
  const handleSetCaptainID = (val) => {
    setCaptainID(val);
    setHasInteracted(true);
  };
  const handleSetChipChoice = (val) => {
    setChipChoice(val);
    setChipsUsed((prev) => ({
      ...prev,
      [currentGameweek]: val,
    }));
    setHasInteracted(true);
  };

  // Recalculate transfersThisGW when team changes, using originalTeam as baseline (symmetric difference, ignore positions)
  useEffect(() => {
    if (!originalTeam) return;
    // Helper to flatten all player IDs in a team object
    const getIds = (t) => POSITIONS.flatMap(pos => t?.[pos] || []);
    const origIdsArr = getIds(originalTeam);
    const currIdsArr = getIds(team);
    // If the team is exactly the same as originalTeam (all IDs match, order doesn't matter), reset transfersThisGW to savedTransfersBase
    const same =
      origIdsArr.length === currIdsArr.length &&
      origIdsArr.every((id) => currIdsArr.includes(id)) &&
      currIdsArr.every((id) => origIdsArr.includes(id));
    if (same) {
      setTransfersThisGW(savedTransfersBase);
      return;
    }
    const origPlayers = new Set(origIdsArr);
    const currPlayers = new Set(currIdsArr);
    // Count players added and removed (symmetric difference)
    let diffCount = 0;
    origPlayers.forEach(id => {
      if (!currPlayers.has(id)) diffCount++;
    });
    currPlayers.forEach(id => {
      if (!origPlayers.has(id)) diffCount++;
    });
    // Each transfer is a pair (out + in), so divide by 2
    const netTransfers = Math.floor(diffCount / 2);
    setTransfersThisGW(savedTransfersBase + netTransfers);
  }, [team, originalTeam, POSITIONS, savedTransfersBase]);

  const [mobilePickerOpen, setMobilePickerOpen] = useState(false);
  const [mobilePickerPosition, setMobilePickerPosition] = useState("DEF");
  const [mobilePickerSort, setMobilePickerSort] = useState("selected");
  const [playerCardOpen, setPlayerCardOpen] = useState(false);
  const [activePlayerCardID, setActivePlayerCardID] = useState(null);
  const [modalPlayerIDs, setModalPlayerIDs] = useState([]);
  // Was hard-coded to true, which hid the "locked" message after the deadline while
  // Save stayed disabled (isLocked) — the page looked editable but couldn't save.
  const mockUnlockMobile = false;

  // === Drawer Stat Formatting Helpers ===
  const getLast3Form = (player) => {
    const history = player?.pointsHistory || {};
    const gwKeys = Object.keys(history).sort((a, b) => {
      const aNum = Number(String(a).replace("GW", "")) || 0;
      const bNum = Number(String(b).replace("GW", "")) || 0;
      return aNum - bNum;
    });
    return gwKeys.slice(-3).map((gw) => Number(history[gw]) || 0);
  };

  const getFormDisplay = (player) => {
    const vals = getLast3Form(player);
    return vals.length ? [...vals].reverse().join(" · ") : "—";
  };

  const getLast3FormTotal = (player) => {
    return getLast3Form(player).reduce((sum, val) => sum + (Number(val) || 0), 0);
  };

  const getTotalPriceChange = (player) => {
    const history = player?.priceHistory || {};
    const gwKeys = Object.keys(history).sort((a, b) => {
      const aNum = Number(String(a).replace("GW", "")) || 0;
      const bNum = Number(String(b).replace("GW", "")) || 0;
      return aNum - bNum;
    });
    if (!gwKeys.length) return 0;
    const first = Number(history[gwKeys[0]]) || 0;
    const current = Number(player?.currentPrice ?? history[gwKeys[gwKeys.length - 1]]) || 0;
    return Number((current - first).toFixed(1));
  };

  const getPerAppearance = (value, appearances) => {
    const num = Number(value) || 0;
    const apps = Number(appearances) || 0;
    if (!apps) return 0;
    return Number((num / apps).toFixed(2));
  };

  // Panel (list + form) content
  const panelContent = (
    <Panel
      style={{
        background: "white",
        boxShadow: "none",
        border: "none",
        borderRadius: "12px",
        padding: "0.1rem 1.5rem",
        width: "100%",
        maxWidth: "420px",
        margin: "0 auto",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <div className="player-list-panel" style={{ marginTop: "0.01rem", minWidth: 0 }}>
        <BodyText style={{ fontSize: "1.3rem", fontWeight: "600", color: "#111", marginBottom: "0.1rem" }}>
          Budget remaining:{" "}
          <span style={{ fontSize: "1.2rem", color: budget < -0.05 ? "#d32f2f" : "#50B4C8" }}>
            {budget < -0.05
              ? `-$${Math.abs(budget).toFixed(1)}m over`
              : `$${Math.abs(budget) < 0.05 ? "0.0" : budget.toFixed(1)}m left`}
          </span>
        </BodyText>

        {teamError && (
          <BodyText
            style={{ color: "#666", fontSize: "0.9rem", marginBottom: "0.3rem", whiteSpace: "pre-line" }}
          >
            {teamError}
          </BodyText>
        )}
        {errorMsg && (
          <BodyText style={{ color: "red", fontSize: "0.9rem", marginBottom: "0.3rem" }}>{errorMsg}</BodyText>
        )}

        {POSITIONS.map((pos, idx) => (
          <React.Fragment key={pos}>
            {idx > 0 && (
              <hr style={{ border: "none", borderBottom: "1px solid #e5e7eb", margin: "0.5rem 0" }} />
            )}
            <div style={{ marginTop: "0.25rem" }}>
              <HeadingMedium style={{ fontSize: "1rem", marginBottom: "0.1rem" }}>{pos}</HeadingMedium>
              <ul style={{ listStyle: "none", paddingLeft: 0 }}>
                {players
                  .filter((p) => p.position === pos)
                  .map((player) => {
                    const checked = team[pos].includes(player.id);
                    const disabled = isLocked || (!checked && wouldBreakCompositionIfAdded(player, pos));
                    const priceDisplay = `$${getEffectivePrice(player, "current")}m`;
                    return (
                      <li key={player.id} style={{ marginBottom: "0.1rem" }}>
                        <label style={{ opacity: disabled ? 0.55 : 1, fontSize: "0.9rem" }}>
                          <input
                            type="checkbox"
                            disabled={disabled}
                            checked={checked}
                            onChange={(e) => toggleInSlot(player.id, pos, e.target.checked)}
                          />
                          {` ${player.name} - ${priceDisplay}`}
                        </label>
                      </li>
                    );
                  })}
              </ul>
            </div>
          </React.Fragment>
        ))}

        <hr style={{ border: "none", borderBottom: "1px solid #e5e7eb", margin: "0.5rem 0" }} />
        <div style={{ marginTop: "0.5rem" }}>
          <label style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.4rem", minWidth: 0 }}>
            <BodyText as="span" style={{ fontSize: "0.9rem" }}>Captain (required):{" "}</BodyText>
            <select
              value={captainID || ""}
              onChange={(e) => handleSetCaptainID(e.target.value)}
              disabled={false} // TEMP
              style={{ fontSize: "0.9rem", maxWidth: "100%" }}
            >
              <option value="" disabled>Select captain…</option>
              {selectedPlayers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.position})
                </option>
              ))}
            </select>
          </label>
        </div>

        {(selectedLeague?.chipsEnabled ?? true) && (
          <>
            <hr style={{ border: "none", borderBottom: "1px solid #e5e7eb", margin: "0.5rem 0" }} />
            <div style={{ marginTop: "0.5rem" }}>
              <BodyText style={{ fontSize: "1rem", fontWeight: "600", marginBottom: "0.4rem" }}>
                Chip (optional):
              </BodyText>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginBottom: "0.3rem" }}>
                {CHIP_OPTIONS.map((opt) => (
                  <label key={opt} style={{ fontSize: "0.9rem" }}>
                    <input
                      type="radio"
                      name="chip"
                      value={opt}
                      checked={chipChoice === opt}
                      onClick={() => handleSetChipChoice(chipChoice === opt ? "None" : opt)}
                      disabled={false} // TEMP
                    />{" "}
                    {opt}
                  </label>
                ))}
              </div>
              <BodyText style={{ fontSize: "0.8rem", color: "#666" }}>
                Each chip can be used once per season.
              </BodyText>
            </div>
          </>
        )}

        <hr style={{ border: "none", borderBottom: "1px solid #e5e7eb", margin: "0.2rem" }} />
        <div style={{ marginTop: "0.5rem" }}>
          <BodyText style={{ fontSize: "0.9rem", marginBottom: "0.4rem"  }}>
            Transfers available: {currentGameweek === startGW || isUnlimitedFT(maxFreeTransfers) ? "unlimited" : maxFreeTransfers}
          </BodyText>
          <BodyText style={{ fontSize: "0.9rem" }}>
            Transfers used: {currentGameweek === startGW ? "unlimited" : transfersThisGW}
          </BodyText>
          <BodyText style={{ fontSize: "0.9rem" }}>
            Penalty: {currentGameweek === startGW
              ? "None"
              : transferPenalty > 0
              ? `-${transferPenalty} pts`
              : "0 pts"}
          </BodyText>
        </div>
      </div>
      {!isLocked ? (
        <div className="pick-team-buttons">
          <PrimaryButton onClick={handleSaveSquad} disabled={saveDisabled} style={{ marginBottom: "0.5rem" }}>
            Save Squad
          </PrimaryButton>
          {currentGameweek !== startGW && (
            <SecondaryButton
              type="button"
              onClick={handleResetToLastWeek}
              disabled={false} // TEMP
              style={{ marginBottom: "0.5rem"}} 
            >
              Reset to Last Week’s Team
            </SecondaryButton>
          )}
        </div>
      ) : (
        <BodyText style={{ color: "red", fontSize: "0.9rem", marginTop: "0.75rem" }}>
          The gameweek is locked. Selections are no longer editable.
        </BodyText>
      )}
    </Panel>
  );

  const totalSelectedCount = POSITIONS.reduce(
    (sum, pos) => sum + ((team[pos] || []).length || 0),
    0
  );
  const maxPlayers = leagueConfig.squadConfig?.maxPlayers ?? 0;
  const minPlayers = leagueConfig.squadConfig?.minPlayers ?? 0;
  const mobileIsLocked = mockUnlockMobile ? false : isLocked;

  const mobileStatusText = (() => {
    if (mobileIsLocked) return "Locked";
    if (errorMsg) return errorMsg;
    if (minPlayers && totalSelectedCount < minPlayers) {
      const remaining = minPlayers - totalSelectedCount;
      return `Select ${remaining} more player${remaining === 1 ? "" : "s"}`;
    }
    if (maxPlayers && totalSelectedCount === maxPlayers) return ""; // only show problems
    return "Keep building";
  })();

  const mobileStatusColor = mobileIsLocked || errorMsg ? "#d32f2f" : "#1f7a3e";

  const mobileTransfersLabel =
    currentGameweek === startGW || isUnlimitedFT(maxFreeTransfers)
      ? "Unlimited FT"
      : `${maxFreeTransfers} FT • ${transfersThisGW} used`;

  // Build priceMap: { [playerId]: currentPrice } for all selected players in team
  useEffect(() => {
    if (mobilePickerPosition !== "ALL" && !POSITIONS.includes(mobilePickerPosition)) {
      setMobilePickerPosition(POSITIONS[0] || "DEF");
    }
  }, [POSITIONS, mobilePickerPosition]);
  const formRankMap = useMemo(() => {
    const sorted = [...players]
      .map((player) => ({
        id: player.id,
        total: getLast3FormTotal(player),
      }))
      .sort((a, b) => b.total - a.total);

    const rankMap = new Map();
    let currentRank = 1;

    sorted.forEach((entry, index) => {
      if (index > 0 && entry.total < sorted[index - 1].total) {
        currentRank = index + 1;
      }
      rankMap.set(entry.id, currentRank);
    });

    return rankMap;
  }, [players]);

  const activePlayerCard = activePlayerCardID ? byId.get(activePlayerCardID) || null : null;
  const squadModalPlayerIDs = POSITIONS.flatMap((pos) => team[pos] || []);
  const allLeaguePlayerIDs = players
    .slice()
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")))
    .map((p) => p.id);

  const openPlayerCard = (playerOrId, playerIDs = []) => {
    const playerID = typeof playerOrId === "string" ? playerOrId : playerOrId?.id;
    if (!playerID) return;
    setActivePlayerCardID(playerID);
    setModalPlayerIDs(playerIDs);
    setPlayerCardOpen(true);
  };


  const closePlayerCard = () => {
    setPlayerCardOpen(false);
    setActivePlayerCardID(null);
    setModalPlayerIDs([]);
    setMobilePickerOpen(true);
  };

  const activeModalIndex = activePlayerCardID ? modalPlayerIDs.indexOf(activePlayerCardID) : -1;
  const canPrevModalPlayer = activeModalIndex > 0;
  const canNextModalPlayer = activeModalIndex >= 0 && activeModalIndex < modalPlayerIDs.length - 1;

  const goToPrevModalPlayer = () => {
    if (!canPrevModalPlayer) return;
    setActivePlayerCardID(modalPlayerIDs[activeModalIndex - 1]);
  };

  const goToNextModalPlayer = () => {
    if (!canNextModalPlayer) return;
    setActivePlayerCardID(modalPlayerIDs[activeModalIndex + 1]);
  };

  const openPlayerDrawerFromCard = (playerOrId) => {
    const playerID = typeof playerOrId === "string" ? playerOrId : playerOrId?.id;
    if (!playerID) return;

    const selectedPlayer = byId.get(playerID);
    if (selectedPlayer?.position) {
      setMobilePickerPosition(selectedPlayer.position);
    }

    setPlayerCardOpen(false);
    setActivePlayerCardID(null);
    setMobilePickerOpen(true);
  };

  const pillStyle = {
    flex: "1 1 0",
    minWidth: 0,
    textAlign: "center",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    background: "#f4f7f8",
    borderRadius: "999px",
    padding: "0.4rem 0.4rem",
    fontSize: "0.82rem",
    fontWeight: "600",
    color: "#234",
  };
  const isOverBudget = budget < -0.05;
  const isOverFT = !mobileIsLocked && transferPenalty > 0;
  const mobileSummaryStrip = (
    <div
      style={{
        width: "100%",
        maxWidth: "420px",
        margin: "0.75rem auto 0",
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: "14px",
        padding: "0.55rem",
        boxSizing: "border-box",
        boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
      }}
    >
      <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
        <div
          style={{
            ...pillStyle,
            fontWeight: 800,
            ...(isOverBudget ? { background: "#fdecec", color: "#d32f2f" } : {}),
          }}
        >
          {isOverBudget
            ? `-$${Math.abs(budget).toFixed(1)}m over`
            : `$${Math.abs(budget) < 0.05 ? "0.0" : budget.toFixed(1)}m left`}
        </div>
        <div style={pillStyle}>
          {totalSelectedCount}/{maxPlayers || "-"} selected
        </div>
        <div style={{ ...pillStyle, ...(isOverFT ? { background: "#fff7e6", color: "#b45309" } : {}) }}>
          {mobileTransfersLabel}
        </div>
      </div>
      {mobileStatusText && !mobileIsLocked && (
        <BodyText style={{ margin: "0.4rem 0.2rem 0", fontSize: "0.8rem", fontWeight: 700, textAlign: "center", color: mobileStatusColor }}>
          {mobileStatusText}
        </BodyText>
      )}
    </div>
  );
  const priceMap = {};
  POSITIONS.forEach(pos => {
    (team[pos] || []).forEach(id => {
      const player = byId.get(id);
      priceMap[id] = getEffectivePrice(player, "current");
    });
  });

  // Build playersForPitch with displayValue entries
  const playersForPitch = new Map();
  players.forEach(p => {
    const cloned = { ...p };
    if (priceMap[p.id] !== undefined) {
      cloned.displayValue = `$${priceMap[p.id].toFixed(1)}`;
    }
    playersForPitch.set(p.id, cloned);
  });

  // Pitch component content
  const pitchContent = (
    <div className="pitch-panel pt-pitch">
      <Pitch
        team={team}
        playersById={playersForPitch}
        positions={POSITIONS}
        chipsUsed={chipsUsed}
        chipChoice={chipChoice}
        onPlayerClick={(playerId) => openPlayerCard(playerId, squadModalPlayerIDs)}
        captainID={captainID}
        currentGameweek={currentGameweek}
        priceMap={priceMap}
        availablePlayerIDs={availablePlayerIDs}
        newPlayerIDs={newPlayerIDs}
        isLocked={mobileIsLocked}
        onMakeCaptain={handleSetCaptainID}
        onAddEditPlayer={openPlayerDrawerFromCard}
        onRemovePlayer={(playerId, slot) => toggleInSlot(playerId, slot, false)}
      />
    </div>
  );


  // ---- Save status UI ----------------------------------------------------
  const nameOf = (id) => byId.get(id)?.name || "?";
  const fmtSavedAt = (d) =>
    d
      ? d.toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" })
      : "";
  const changeSummary = (() => {
    const parts = [];
    const n = Math.max(playersIn.length, playersOut.length);
    for (let i = 0; i < n; i++) {
      const o = playersOut[i], inn = playersIn[i];
      if (o && inn) parts.push(`${nameOf(o)} → ${nameOf(inn)}`);
      else if (inn) parts.push(`+ ${nameOf(inn)}`);
      else if (o) parts.push(`− ${nameOf(o)}`);
    }
    if (captainChanged && captainID) parts.push(`Captain: ${nameOf(captainID)}`);
    if (chipChanged) parts.push(chipChoice && chipChoice !== "None" ? `Chip: ${chipChoice}` : "Chip removed");
    return parts.join(" · ");
  })();
  const stickySaveLabel =
    saveInfo?.kind === "empty"
      ? "Save team"
      : "Save changes";

  const statusStyles = {
    saved: { bg: "#ecfdf3", fg: "#15803d", dot: "#15803d" },
    carried: { bg: "#eef7fa", fg: "#1f6f80", dot: "#50B4C8" },
    dirty: { bg: "#fff7e6", fg: "#b45309", dot: "#f59e0b" },
    locked: { bg: "#f3f4f6", fg: "#4b5563", dot: "#9ca3af" },
  };
  const statusLine = (() => {
    if (!saveInfo || !currentGameweek) return null;
    let kind, text, right = "";
    if (mobileIsLocked) {
      kind = "locked";
      text = "Deadline passed. Your team is locked.";
    } else if (isDirty) {
      kind = "dirty";
      text = "Unsaved changes. Tap Save before you leave.";
    } else if (saveInfo.kind === "saved") {
      kind = "saved";
      text = "Team saved";
      right = fmtSavedAt(saveInfo.at);
    } else if (saveInfo.kind === "carried") {
      kind = "carried";
      text = `${saveInfo.from} team carried over`;
    } else {
      kind = "dirty";
      text = `No team saved for ${currentGameweek} yet.`;
    }
    const st = statusStyles[kind];
    return (
      <div
        role="status"
        style={{
          width: "100%",
          maxWidth: "420px",
          margin: "0.35rem auto 0",
          display: "flex",
          alignItems: "center",
          gap: "0.45rem",
          background: st.bg,
          color: st.fg,
          borderRadius: "10px",
          padding: "0.5rem 0.75rem",
          fontSize: "0.84rem",
          fontWeight: 600,
          boxSizing: "border-box",
        }}
      >
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: st.dot, flex: "none" }} />
        <span>{text}</span>
        {right && <span style={{ marginLeft: "auto", fontWeight: 500, opacity: 0.85, whiteSpace: "nowrap" }}>{right}</span>}
      </div>
    );
  })();

  const showSticky = isDirty && !mobileIsLocked;
  const isCleanSaved = !isDirty && (saveInfo?.kind === "saved" || saveInfo?.kind === "carried");
  const saveHint =
    showSticky &&
    (saveDisabled && (errorMsg || mobileStatusText) ? (
      <span style={{ color: "#d32f2f" }}>{errorMsg || mobileStatusText}</span>
    ) : (
      <>
        {changeSummary}
        {isOverFT && (
          <span style={{ color: "#d32f2f", fontWeight: 700 }}>{changeSummary ? " · " : ""}−{transferPenalty} pts</span>
        )}
      </>
    ));
  const actionBar = (
    <div className="pt-sticky-save">
      <div style={{ maxWidth: "420px", margin: "0 auto" }}>
        <div style={{ display: "flex", gap: "0.55rem", alignItems: "stretch" }}>
          <PrimaryButton
            type="button"
            onClick={() => setMobilePickerOpen(true)}
            disabled={mobileIsLocked}
            style={{ flex: 1, fontSize: "0.82rem", padding: "0.7rem 0.4rem", whiteSpace: "nowrap" }}
          >
            Edit Players
          </PrimaryButton>
          {mobileIsLocked || isCleanSaved ? (
            <button
              type="button"
              disabled
              style={{ flex: 1.6, background: "#e5e7eb", color: "#6b7280", border: "none", borderRadius: "8px", fontWeight: 700, fontSize: "0.92rem", fontFamily: "inherit", cursor: "default" }}
            >
              {mobileIsLocked ? "Locked" : "Saved ✓"}
            </button>
          ) : (
            <SecondaryButton
              type="button"
              onClick={handleSaveSquad}
              disabled={saveDisabled}
              style={{ flex: 1.6, fontSize: "0.92rem", padding: "0.7rem 0.4rem", whiteSpace: "nowrap", ...(saveDisabled ? { opacity: 0.5, cursor: "not-allowed" } : {}) }}
            >
              {stickySaveLabel}
            </SecondaryButton>
          )}
        </div>
        {saveHint && (
          <div style={{ fontSize: "0.75rem", textAlign: "center", marginTop: "0.3rem", color: "#6b7280", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {saveHint}
          </div>
        )}
      </div>
    </div>
  );

  const leaveSheet = pendingNav && (
    <div
      onClick={() => setPendingNav(null)}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#fff", width: "100%", maxWidth: "480px", borderRadius: "18px 18px 0 0", padding: "1.1rem 1rem calc(1.2rem + env(safe-area-inset-bottom))", boxSizing: "border-box" }}
      >
        <HeadingMedium style={{ margin: "0 0 0.35rem", fontSize: "1.05rem" }}>Save your team?</HeadingMedium>
        <BodyText style={{ margin: "0 0 0.9rem", fontSize: "0.88rem", color: "#6b7280" }}>
          {changeSummary ? `${changeSummary}${isOverFT ? ` (−${transferPenalty} pts)` : ""}. ` : ""}These changes aren't saved. If you leave now, your {currentGameweek} team stays as it was.
        </BodyText>
        <SecondaryButton
          type="button"
          disabled={saveDisabled}
          onClick={() => { setPendingNav(null); handleSaveSquad(); }}
          style={{ width: "100%", ...(saveDisabled ? { opacity: 0.5 } : {}) }}
        >
          {saveDisabled && errorMsg ? `Can't save: ${errorMsg}` : "Save team"}
        </SecondaryButton>
        <button
          type="button"
          onClick={() => setPendingNav(null)}
          style={{ width: "100%", marginTop: "0.5rem", background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: "8px", padding: "0.7rem", fontWeight: 700, fontFamily: "inherit", color: "#374151", cursor: "pointer" }}
        >
          Keep editing
        </button>
        <button
          type="button"
          onClick={() => { const go = pendingNav; setPendingNav(null); setNavGuard(null); go(); }}
          style={{ width: "100%", marginTop: "0.35rem", background: "none", border: "none", padding: "0.7rem", fontWeight: 700, fontFamily: "inherit", color: "#d32f2f", cursor: "pointer" }}
        >
          Leave without saving
        </button>
      </div>
    </div>
  );

  const mobileV2Content = (
    <div style={{ width: "100%" }}>
      {statusLine}
      <div
        style={{
          marginTop: "0.25rem",
          width: "100%",
          maxWidth: "390px",
          marginLeft: "auto",
          marginRight: "auto",
          position: "relative",
        }}
      >
        {pitchContent}
      </div>

      {mobileSummaryStrip}

      {/* One compact row: chip (left) · undo / reset (right) */}
      {(() => {
        const chipsOn = selectedLeague?.chipsEnabled ?? true;
        const showUndo = !mobileIsLocked && isDirty;
        const showReset = !mobileIsLocked && !isDirty && currentGameweek !== startGW && saveInfo?.kind === "saved";
        if (!chipsOn && !showUndo && !showReset) return null;
        return (
          <div
            style={{
              width: "100%",
              maxWidth: "420px",
              margin: "0.55rem auto 0",
              padding: "0 0.15rem",
              boxSizing: "border-box",
              display: "flex",
              flexWrap: chipExpanded ? "wrap" : "nowrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "0.5rem",
            }}
          >
            <div style={{ flex: chipExpanded ? "1 1 100%" : "0 1 auto", minWidth: 0 }}>
              {chipsOn && (
                chipChoice !== "None" ? (
                /* Chip active — show pill badge with × */
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#e8f6f9", border: "1px solid #50B4C8", borderRadius: "999px", padding: "0.3rem 0.5rem 0.3rem 0.75rem", fontSize: "0.82rem", fontWeight: 600, color: "#50B4C8" }}>
                  {chipChoice}
                  <button
                    onClick={() => { handleSetChipChoice("None"); setChipExpanded(false); }}
                    style={{ background: "none", border: "none", cursor: "pointer", padding: "0 2px", color: "#50B4C8", fontSize: "1rem", lineHeight: 1, fontFamily: "inherit" }}
                    aria-label="Remove chip"
                  >×</button>
                </div>
              ) : chipExpanded ? (
                /* Expanded — show chip options */
                <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "0 0.15rem" }}>
                    <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "#234" }}>Play a chip this week</span>
                    <span style={{ fontSize: "0.75rem", color: "#888" }}>Each chip once per season</span>
                  </div>
                  {CHIP_OPTIONS.filter(opt => opt !== "None").map((opt) => {
                    const usedEarlier = Object.values(chipsUsed || {}).includes(opt) && chipsUsed[currentGameweek] !== opt;
                    const usedGW = usedEarlier ? Object.keys(chipsUsed || {}).find((gw) => chipsUsed[gw] === opt) : null;
                    const CHIP_INFO = {
                      "Goal Boost": { icon: "⚽", desc: "Double points for goals & assists" },
                      "Triple Captain": { icon: "3×", desc: "Captain scores 3x instead of 2x" },
                      "Fortress": { icon: "🛡", desc: "Double points for clean sheets" },
                    };
                    const info = CHIP_INFO[opt] || { icon: "★", desc: "" };
                    return (
                      <button
                        key={opt}
                        onClick={() => { if (!usedEarlier && !mobileIsLocked) { handleSetChipChoice(opt); setChipExpanded(false); } }}
                        disabled={usedEarlier || mobileIsLocked}
                        style={{ display: "flex", alignItems: "center", gap: "0.7rem", width: "100%", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "12px", padding: "0.55rem 0.75rem", cursor: usedEarlier ? "default" : "pointer", opacity: usedEarlier ? 0.5 : 1, textAlign: "left", fontFamily: "inherit", boxShadow: usedEarlier ? "none" : "0 2px 8px rgba(0,0,0,0.04)" }}
                      >
                        <span style={{ width: 34, height: 34, flex: "none", borderRadius: "50%", background: "#e8f6f9", color: "#2c8ea3", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1rem", fontWeight: 800 }}>
                          {info.icon}
                        </span>
                        <span style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
                          <span style={{ fontSize: "0.9rem", fontWeight: 700, color: "#234" }}>{opt}</span>
                          <span style={{ fontSize: "0.78rem", color: "#6b7280", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {usedEarlier ? `Used in ${usedGW}` : info.desc}
                          </span>
                        </span>
                        {!usedEarlier && <span style={{ color: "#50B4C8", fontSize: "1.1rem", fontWeight: 700, flex: "none" }}>›</span>}
                      </button>
                    );
                  })}
                  <button onClick={() => setChipExpanded(false)} style={{ alignSelf: "center", background: "none", border: "none", color: "#888", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer", padding: "0.2rem 0.5rem", fontFamily: "inherit" }}>
                    Cancel
                  </button>
                </div>
              ) : (
                /* Collapsed — show text link */
                <button
                  onClick={() => !mobileIsLocked && setChipExpanded(true)}
                  disabled={mobileIsLocked}
                  style={{ background: "none", border: "none", padding: 0, color: "#50B4C8", fontSize: "0.85rem", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                >
                  ＋ Use a chip
                </button>
              )
              )}
            </div>
            {!chipExpanded && showUndo && (
              <button type="button" onClick={handleUndoChanges} style={{ background: "none", border: "none", padding: "0.2rem 0", color: "#6b7280", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline", whiteSpace: "nowrap" }}>
                Undo changes
              </button>
            )}
            {!chipExpanded && showReset && (
              <button type="button" onClick={handleResetToLastWeek} style={{ background: "none", border: "none", padding: "0.2rem 0", color: "#6b7280", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline", whiteSpace: "nowrap" }}>
                Reset to last week’s team
              </button>
            )}
          </div>
        );
      })()}

      {mobileIsLocked && (
        <div
          style={{
            width: "100%",
            maxWidth: "420px",
            margin: "0.75rem auto 0",
            background: "#fff2f2",
            border: "1px solid #ffd1d1",
            borderRadius: "12px",
            padding: "0.85rem 0.95rem",
            boxSizing: "border-box",
          }}
        >
          <BodyText style={{ color: "#d32f2f", fontSize: "0.9rem", margin: 0 }}>
            The gameweek is locked. Selections are no longer editable.
          </BodyText>
        </div>
      )}

      {/* room for the fixed action bar */}
      <div style={{ height: showSticky ? "88px" : "70px" }} aria-hidden="true" />

      <MobilePlayerDrawer
        open={mobilePickerOpen}
        onClose={() => setMobilePickerOpen(false)}
        positions={POSITIONS}
        players={players}
        team={team}
        budget={budget}
        maxPlayers={maxPlayers}
        totalSelectedCount={totalSelectedCount}
        mobilePickerPosition={mobilePickerPosition}
        setMobilePickerPosition={setMobilePickerPosition}
        mobilePickerSort={mobilePickerSort}
        setMobilePickerSort={setMobilePickerSort}
        mobileIsLocked={mobileIsLocked}
        wouldBreakCompositionIfAdded={wouldBreakCompositionIfAdded}
        toggleInSlot={toggleInSlot}
        getEffectivePrice={getEffectivePrice}
        captainID={captainID}
        handleSetCaptainID={handleSetCaptainID}
        formRankMap={formRankMap}
        onPlayerOpen={(playerId) => openPlayerCard(playerId, allLeaguePlayerIDs)}
        availablePlayerIDs={availablePlayerIDs}
      />

      <PlayerCardModal
        open={playerCardOpen}
        onClose={closePlayerCard}
        onAddEdit={openPlayerDrawerFromCard}
        player={activePlayerCard}
        onPrev={goToPrevModalPlayer}
        onNext={goToNextModalPlayer}
        canPrev={canPrevModalPlayer}
        canNext={canNextModalPlayer}
        players={players}
        getEffectivePrice={getEffectivePrice}
        formRank={activePlayerCard ? formRankMap.get(activePlayerCard.id) : null}
        leagueID={leagueID}
        availablePlayerIDs={availablePlayerIDs}
      />
    </div>
  );

  return (
    <>
      <LoadingOverlay show={isSaving} message="Saving squad…" />
      {actionBar}
      {leaveSheet}
      {selectedLeague && team?.name && (
        <HeadingMedium style={{ textAlign: "center", marginTop: "0.0rem", color: "#555" }}>
          {selectedLeague?.name || "My Team"}
        </HeadingMedium>
      )}
      {mobileV2Content}
    </>
  );
}